import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faHandshake, faShieldCheck, faUserTie } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { trackDemoRequestClick } from "@/utils/analytics";
import { ROUTE_CONFIG } from "@/routes/config";

import LandingCertificationLogos from "../Home/LandingSections/LandingCertificationLogos";

type ValueItem = {
	title: string;
	description: string;
};

const VALUE_ICONS = [faUserTie, faShieldCheck, faHandshake];

export default function About() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const ts = (key: string) => t(`marketing.about.${key}`);

	const values = t("marketing.about.values", { returnObjects: true }) as ValueItem[];

	const handleContact = () => {
		trackDemoRequestClick("about_page");
		navigate(ROUTE_CONFIG.CONTACT);
	};

	return (
		<div className="w-full flex flex-col items-center font-urbanist">
			<section className="w-full px-4 md:px-16">
				<div className="max-w-[1420px] mx-auto py-16 sm:py-24 flex flex-col gap-16">
					<div className="flex flex-col items-center text-center gap-6">
						<h1 className="text-[36px] md:text-[48px] font-bold leading-[120%] text-[#1D243C] max-w-[900px]">
							{ts("title")}
						</h1>
						<p className="text-[18px] leading-[150%] text-[#626373] max-w-[820px]">
							{ts("subtitle")}
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						{values?.map((value, index) => (
							<div
								key={value.title}
								className="flex flex-col gap-4 bg-white border border-primary-gray-lightest rounded-lg px-6 py-6"
							>
								<div className="bg-[#F0F4FF] flex w-12 h-12 rounded-md justify-center items-center">
									<Icon className="w-5 h-5 text-primary-blue" icon={VALUE_ICONS[index]} />
								</div>
								<h2 className="text-[20px] font-bold text-[#1D243C]">{value.title}</h2>
								<p className="text-[#626373] text-[16px] leading-[150%]">{value.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className="w-full px-4 md:px-16 bg-[#F7F8FB]">
				<div className="max-w-[1420px] mx-auto py-16 sm:py-24 flex flex-col gap-10 items-center text-center">
					<h2 className="text-[32px] md:text-[40px] font-semibold leading-[120%] text-[#1D243C]">
						{ts("certificationsTitle")}
					</h2>
					<p className="text-[18px] leading-[150%] text-[#626373] max-w-[760px]">
						{ts("certificationsText")}
					</p>
					<LandingCertificationLogos className="w-full border-y-[2px] border-[#E6E6EC] flex flex-col sm:flex-row items-center justify-center opacity-90" />
				</div>
			</section>

			<section className="w-full px-4 md:px-16">
				<div className="max-w-[1420px] mx-auto py-16 sm:py-24 flex flex-col items-center text-center gap-8">
					<h2 className="text-[32px] md:text-[40px] font-semibold leading-[120%] text-[#1D243C] max-w-[820px]">
						{ts("consultingTitle")}
					</h2>
					<p className="text-[18px] leading-[150%] text-[#626373] max-w-[760px]">
						{ts("consultingText")}
					</p>
					<button
						className="px-10 py-3 rounded-xl bg-[#4680FC] hover:bg-[#336de0] transition-colors text-white text-[16px] font-medium"
						type="button"
						onClick={handleContact}
					>
						{ts("consultingCta")}
					</button>
				</div>
			</section>
		</div>
	);
}
